/* eslint-disable react/jsx-no-literals */
/* eslint-disable i18next/no-literal-string */
import {ComboboxCustomSearchInput} from '#/atoms/search-bar';
import {ShareLandLogoLink} from '#/components/header/share-land-logo-link';
import {HeaderContentLayout} from '#/components/layout/layout';
import {Page} from '#/pages/page';
import {ExchangeRoute} from '#/routes/exchange/exchange-route';
import {DefaultChildren} from '#/utils/utils';
import {ArrowLeftIcon} from '@heroicons/react/24/solid';
import {Combobox} from '@reach/combobox';
import {useQuery} from '@tanstack/react-query';
import {ChangeEvent, FunctionComponent, useState} from 'react';
import {Link} from 'react-router-dom';
import {CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';
import {OracleValidationLandOutput, OracleValidationOutput} from 'shareland-common';

const graphColoursList = ['blue', 'orange', 'green', 'purple', 'red'];

const OracleValidationPage: FunctionComponent<DefaultChildren> = () => {
  const [searchValue, setSearchValue] = useState('');

  const [file, setFile] = useState<File>();
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFile(e.target.files[0]);
    }
  };

  const {data: oracleValidationOutput} = useQuery({
    queryKey: ['file'],
    queryFn: async () => {
      const text = await file!.text();
      return JSON.parse(text) as OracleValidationOutput;
    },
    enabled: !!file,
  });

  const filteredOracleLands = oracleValidationOutput?.lands.filter(land =>
    land.name.toLowerCase().includes(searchValue.toLowerCase()),
  );

  return (
    <Page title='Oracle Validation - Shareland'>
      <HeaderContentLayout className='bg-gray-50 h-full overflow-hidden'>
        <header className={'h-header w-full bg-black px-5 py-3'}>
          <div className='w-full h-full flex justify-between items-center gap-4 max-w-screen-3xl mx-auto'>
            <div className='grow flex h-full items-center gap-4'>
              <Link className='w-[24px]' to={ExchangeRoute.HOME}>
                <ArrowLeftIcon className='w-full aspect-square fill-white stroke-white' />
              </Link>
              <ShareLandLogoLink />
            </div>

            <div>
              <input
                className='p-1 file:focus:outline-none file:bg-gray-500 file:text-white file:border-0 file:rounded file:mr-2 file:cursor-pointer file:hover:opacity-70 cursor-pointer block w-full text-sm text-gray-300 border border-gray-300 rounded-lg bg-transparent focus:outline-none'
                type='file'
                onChange={handleFileChange}
              />
            </div>
          </div>
        </header>

        {!oracleValidationOutput ? (
          <div className='text-3xl font-semibold p-16'>Analysis will show up here once a file is uploaded.</div>
        ) : (
          <div className='flex flex-col md:flex-row gap-16 overflow-scroll md:overflow-hidden px-16 py-8 md:py-0'>
            <div className='md:overflow-y-scroll md:py-8'>
              <div className='text-2xl text-black font-semibold mb-2'>Filter Settings</div>
              <CodeSection className='text-xs' code={oracleValidationOutput.houseRecordFilterSettings} />

              <div className='mt-16 text-2xl text-black font-semibold mb-2'>Simulation Settings</div>
              <CodeSection className='text-xs' code={oracleValidationOutput.simulationSettings} />
            </div>

            <div className='grow md:overflow-hidden md:py-8'>
              <div className='flex justify-between pb-4 border-b border-gray-300'>
                <div className='grow text-2xl text-black font-semibold mb-2'>Locations</div>

                <Combobox aria-label='Land' className={'flex-1 relative max-w-[300px]'}>
                  <ComboboxCustomSearchInput
                    style={{
                      backgroundColor: 'bg-gray-50',
                      textColor: 'text-black',
                      ringColorInactive: 'border-bg-gray-50',
                      ringColorActive: 'border-deepblue',
                      iconColor: 'fill-black stroke-black',
                    }}
                    placeholder='Search Locations...'
                    value={searchValue}
                    onChange={setSearchValue}
                    variant={'light'}
                  />
                </Combobox>
              </div>

              <div className='h-full md:overflow-y-scroll flex flex-col pb-16 pt-8 gap-16'>
                {filteredOracleLands &&
                  filteredOracleLands.map((land, index) => {
                    return (
                      <div className='flex flex-col gap-6' key={index}>
                        <div className='w-full text-center font-semibold text-xl'>{land.name}</div>
                        <OracleValidationGraph oracleValidationLand={land} models={oracleValidationOutput.models} />
                        <OracleValidationObservationError
                          oracleValidationLand={land}
                          models={oracleValidationOutput.models}
                        />
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        )}
      </HeaderContentLayout>
    </Page>
  );
};

const CodeSection: FunctionComponent<{className?: string; code: Record<any, any>}> = ({className, code}) => (
  <pre className={className}>{JSON.stringify(code, undefined, 2)}</pre>
);

const OracleValidationObservationError: FunctionComponent<{
  oracleValidationLand: OracleValidationLandOutput;
  models: OracleValidationOutput['models'];
}> = ({oracleValidationLand, models}) => {
  return (
    <div className='w-full flex flex-wrap gap-12 justify-start'>
      <div>
        <div className='w-full text-left font-bold'>Observation</div>
        <CodeSection className='text-xs' code={oracleValidationLand.observation} />
      </div>
      <div>
        <div className='w-full text-left font-bold'>Errors</div>
        <div className='text-xs'>
          {oracleValidationLand.statErrors.map((error, index) => {
            return (
              <div className='flex gap-2' key={index}>
                <span>
                  {'> '}
                  {Object.keys(error)[0]}
                </span>
                {Object.values(error)[0].map((error, index) => {
                  return (
                    <span className='' key={index}>
                      {models[index]}={error}
                    </span>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const OracleValidationGraph: FunctionComponent<{
  oracleValidationLand: OracleValidationLandOutput;
  models: OracleValidationOutput['models'];
}> = ({oracleValidationLand, models}) => {
  return (
    <ResponsiveContainer width='100%' height={500}>
      <LineChart
        data={oracleValidationLand.results.map(result => ({
          date: result.date,
          ...Object.fromEntries(models.map((model, index) => [model, result.values[index]])),
        }))}
        margin={{
          top: 5,
          right: 5,
          left: 5,
          bottom: 5,
        }}
      >
        <CartesianGrid strokeDasharray='3 3' />

        <XAxis
          minTickGap={100}
          dataKey='date'
          domain={['dataMin', 'dataMax']}
          tickFormatter={(date: string) => new Date(date).toLocaleDateString()}
        />
        <YAxis
          domain={[(dataMin: any) => Math.floor(dataMin / 100) * 100, (dataMin: any) => Math.ceil(dataMin / 100) * 100]}
        />
        <Tooltip />
        <Legend />
        {models.map((model, index) => (
          <Line
            isAnimationActive={false}
            strokeWidth={2}
            type='monotone'
            dataKey={model}
            stroke={graphColoursList[index]}
            dot={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};

export {OracleValidationPage};
